import { useState, useCallback } from "react";
import { Minimize2, Upload, Download, Trash2, FileText, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "How much can I reduce my PDF file size?",
    answer: "Depending on the content, you can reduce PDF size by 50-90%. PDFs with images benefit most from compression."
  },
  {
    question: "Will compression affect PDF quality?",
    answer: "High quality compression maintains excellent readability. Medium and maximum compression may slightly reduce image quality but keep text sharp."
  },
  {
    question: "Is this PDF compressor free?",
    answer: "Yes, completely free with no signup required. Compress unlimited PDFs without any cost."
  },
  {
    question: "What's the maximum file size I can compress?",
    answer: "You can upload PDFs up to 50MB for compression. For larger files, consider splitting them first."
  },
  {
    question: "Are my compressed files stored?",
    answer: "No, all files are automatically deleted after 30 minutes for your privacy and security."
  }
];

const PdfCompressPage = () => {
  const [file, setFile] = useState(null);
  const [quality, setQuality] = useState("medium");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPreview(response.data);
      }
    } catch (error) {
      console.error("Preview error:", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
      setPreview(null);
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
      setPreview(null);
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleCompress = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("quality", quality);

    try {
      const response = await axios.post(`${API}/pdf-compress`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Compression failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "compressed.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPreview(null);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <>
      <SEOHead
        title="Compress PDF Online Free | Reduce PDF Size"
        description="Compress PDF files to reduce size by up to 90%. Free online PDF compressor. No signup required. Maintain quality while shrinking file size."
        canonicalPath="/pdf-compress"
        keywords="compress PDF, reduce PDF size, PDF compressor free, shrink PDF, online PDF compression, make PDF smaller"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <Minimize2 className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Compress PDF Online Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Reduce PDF file size by up to 90% while maintaining quality. Perfect for email attachments, 
              web uploads, and saving storage space. No signup required.
            </p>
          </div>

        {/* Upload Area */}
        {!result && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#FF3B30]" />
                  </div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {formatSize(file.size)}
                    {preview && ` • ${preview.total_pages} pages`}
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Preview Section */}
            {isLoadingPreview && (
              <div className="bg-card border border-border p-6 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#FF3B30]" />
                <p className="text-sm text-muted-foreground">Loading preview...</p>
              </div>
            )}

            {preview && !isLoadingPreview && (
              <div className="bg-card border border-border p-4" data-testid="preview-section">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-[#FF3B30]" />
                  <span className="text-sm font-medium">Preview ({preview.total_pages} pages)</span>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {preview.pages.slice(0, 8).map((page, index) => (
                    <div key={index} className="relative aspect-[3/4] bg-secondary overflow-hidden border border-border">
                      <img
                        src={`${API}/download/${preview.preview_id}/${page.thumbnail.split('/').pop()}`}
                        alt={`PDF page ${page.page} thumbnail for compression preview`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-0.5">
                        {page.page}
                      </span>
                    </div>
                  ))}
                  {preview.total_pages > 8 && (
                    <div className="aspect-[3/4] bg-secondary flex items-center justify-center border border-border">
                      <span className="text-sm text-muted-foreground">+{preview.total_pages - 8}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quality Options */}
            <div className="border border-border p-6 space-y-4">
              <h3 className="font-medium text-lg">Compression Level</h3>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger className="w-full rounded-sm" data-testid="quality-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High Quality (Less Compression)</SelectItem>
                  <SelectItem value="medium">Balanced (Recommended)</SelectItem>
                  <SelectItem value="low">Maximum Compression</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {quality === "high" && "Best for documents with images that need to stay sharp"}
                {quality === "medium" && "Good balance between file size and quality"}
                {quality === "low" && "Smallest file size, some quality loss in images"}
              </p>
            </div>

            {/* Compress Button */}
            <Button
              onClick={handleCompress}
              disabled={!file || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="compress-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Compressing...
                </>
              ) : (
                <>
                  <Minimize2 className="w-5 h-5 mr-2" />
                  Compress PDF
                </>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8">
              <div className="flex items-center justify-center gap-8 mb-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Original</p>
                  <p className="text-2xl font-semibold">{formatSize(result.files[0]?.original_size)}</p>
                </div>
                <div className="text-4xl text-muted-foreground">→</div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Compressed</p>
                  <p className="text-2xl font-semibold text-[#FF3B30]">{formatSize(result.files[0]?.compressed_size)}</p>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 p-4 text-center mb-6">
                <p className="text-green-800 font-medium text-lg">
                  Reduced by {result.files[0]?.reduction}%
                </p>
              </div>

              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Compress Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-compress" content={toolContent["pdf-compress"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-compress" />
      </div>
    </div>
    </>
  );
};

export default PdfCompressPage;
